import {
  useLayoutEffect,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from 'react';
import type { CharacterId } from '../../../../domain/ids/ids';
import type { GiantId } from '../../../../domain/ids/ids';
import type { RunnerLevel, RunnerPlayer } from '../../../../domain/runner/runnerTypes';
import { VIEWPORT_WIDTH } from '../../../../domain/Constants';
import {
  containFitScale,
  coverFitScale,
  isDesktopHost,
} from '../../../../infrastructure/runtime/fitViewport';
import { useAppFit } from '../../../hooks/useAppFit';
import { LEVEL_GAMEPLAY_Z_INDEX, LEVEL_PLAYFIELD_HEIGHT_PX, LEVEL_FOREGROUND_ENABLED } from './gameplayConstants';
import {
  computeRunnerFallbackLayout,
  computeRunnerLevelLayout,
  computeStartCameraLeadS,
  createPlayfieldConfig,
  worldTranslateXPx,
  type LevelCanvasLayout,
} from './layout/runnerLevelLayout';
import { getLevelVisualAssets } from './levelVisualAssets';
import { LEVEL_GIANT_PLACEMENTS } from './levelGiantPlacements';
import { LevelPanoramaTiles } from './LevelPanoramaTiles';
import { LevelWorldSprites } from './LevelWorldSprites';
import { LevelPlayerSprite } from './LevelPlayerSprite';
import { LevelHitboxOverlay } from './LevelHitboxOverlay';
import {
  applyPlayfieldCamera,
  type PlayfieldMotionBindings,
} from './playfieldMotionBindings';
import './LevelPlayfield.css';

type PlayfieldPointerHandlers = {
  onPointerDown?: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerMove?: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerUp?: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerCancel?: (event: ReactPointerEvent<HTMLDivElement>) => void;
};

type LevelPlayfieldProps = {
  giantId: GiantId;
  level: RunnerLevel;
  player: RunnerPlayer;
  /** Наклон спрайта: -1..1 (доля от максимального угла). */
  tilt: number;
  characterId: CharacterId;
  /** Пауза / countdown: заморозить мигание после удара. */
  isPaused?: boolean;
  pointerHandlers?: PlayfieldPointerHandlers;
  motionBindings?: RefObject<PlayfieldMotionBindings | null>;
};

function LevelPlayfieldLayers({
  layout,
  level,
  player,
  tilt,
  characterId,
  giantId,
  isPaused,
  translateX,
  motionBindings,
}: {
  layout: LevelCanvasLayout;
  level: RunnerLevel;
  player: RunnerPlayer;
  tilt: number;
  characterId: CharacterId;
  giantId: GiantId;
  isPaused: boolean;
  translateX: number;
  motionBindings?: RefObject<PlayfieldMotionBindings | null>;
}) {
  const assets = getLevelVisualAssets(giantId);
  const giantPlacement = LEVEL_GIANT_PLACEMENTS[giantId];
  const artWidth = level.viewBox.width;
  const artHeight = level.viewBox.height;
  const fit = useAppFit();
  // Undo `.App` stretch: world uses contain; sky/panorama/giant use cover.
  const contain = containFitScale(fit.scale);
  const cover = coverFitScale(fit.scale);
  const containStyle = {
    '--fit-contain-inv-x': String(contain.invX),
    '--fit-contain-inv-y': String(contain.invY),
    '--fit-contain-uniform': String(contain.uniform),
  } as CSSProperties;
  const coverStyle = {
    '--fit-cover-inv-x': String(cover.invX),
    '--fit-cover-inv-y': String(cover.invY),
    '--fit-cover-uniform': String(cover.uniform),
  } as CSSProperties;

  if (motionBindings?.current != null) {
    const bindings = motionBindings.current;
    bindings.canvasTopPx = layout.canvasTopPx;
    bindings.displayScale = layout.displayScale;
    bindings.giantPlacement = giantPlacement;
    bindings.routeEndS = level.routeEndS;
    bindings.spawnSvgX = level.spawnSvgX;
    bindings.startPointSvgX = level.startPointSvgX;
    bindings.viewBoxHeight = level.viewBox.height;
  }

  // Seed camera once per level attempt; RAF owns continuous translateX after that.
  useLayoutEffect(() => {
    if (motionBindings?.current != null) {
      applyPlayfieldCamera(motionBindings.current, translateX);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount/attempt seed only
  }, [giantId, level.startPointSvgX, motionBindings]);

  return (
    <>
      <div className="level-playfield__bg-cover" style={coverStyle}>
        <div
          className="level-playfield__sky"
          style={{
            zIndex: LEVEL_GAMEPLAY_Z_INDEX.BACKGROUND,
            background: assets.skyBackground,
          }}
        />
        <LevelPanoramaTiles
          tiles={assets.backgroundTiles}
          artWidth={artWidth}
          artHeight={artHeight}
          translateX={translateX}
          displayScale={layout.displayScale}
          canvasTopPx={layout.canvasTopPx}
          mode="bg"
          zIndex={LEVEL_GAMEPLAY_Z_INDEX.BACKGROUND}
          layerRef={(el) => {
            if (motionBindings?.current != null) {
              motionBindings.current.bgPanorama = el;
            }
          }}
        />
        <img
          ref={(el) => {
            if (motionBindings?.current != null) {
              motionBindings.current.giant = el;
            }
          }}
          className="level-playfield__giant"
          src={assets.giantUrl}
          alt=""
          draggable={false}
          style={{
            zIndex: LEVEL_GAMEPLAY_Z_INDEX.BACKGROUND,
            width: giantPlacement.width,
            height: giantPlacement.height,
            // transform owned by RAF / useLayoutEffect via motionBindings
          }}
        />
      </div>
      <div className="level-playfield__world" style={containStyle}>
        <div
          ref={(el) => {
            if (motionBindings?.current != null) {
              motionBindings.current.worldCamera = el;
            }
          }}
          className="level-playfield__world-camera"
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
          }}
        >
          <LevelWorldSprites
            level={level}
            layout={layout}
            motionBindings={motionBindings}
          />
          <LevelPlayerSprite
            level={level}
            player={player}
            layout={layout}
            tilt={tilt}
            characterId={characterId}
            isHitFlashing={player.hitInvulnerableRemainingSeconds > 0}
            isPaused={isPaused}
            motionBindings={motionBindings}
          />
          <LevelHitboxOverlay level={level} player={player} layout={layout} />
        </div>
        {LEVEL_FOREGROUND_ENABLED && assets.foregroundTiles != null ? (
          <LevelPanoramaTiles
            tiles={assets.foregroundTiles}
            artWidth={artWidth}
            artHeight={artHeight}
            translateX={translateX}
            displayScale={layout.displayScale}
            canvasTopPx={layout.canvasTopPx}
            mode="fg"
            zIndex={LEVEL_GAMEPLAY_Z_INDEX.FOREGROUND}
            layerRef={(el) => {
              if (motionBindings?.current != null) {
                motionBindings.current.fgPanorama = el;
              }
            }}
          />
        ) : null}
      </div>
    </>
  );
}

export function LevelPlayfield({
  giantId,
  level,
  player,
  tilt,
  characterId,
  isPaused = false,
  pointerHandlers,
  motionBindings,
}: LevelPlayfieldProps) {
  const desktopHost = isDesktopHost();
  const playfield = createPlayfieldConfig(
    VIEWPORT_WIDTH,
    LEVEL_PLAYFIELD_HEIGHT_PX,
    desktopHost,
  );
  const cameraLeadS =
    player != null
      ? computeStartCameraLeadS(level, playfield, player, desktopHost)
      : 0;
  const layout =
    player != null
      ? computeRunnerLevelLayout(level, playfield, player, cameraLeadS, desktopHost)
      : computeRunnerFallbackLayout(level, playfield, desktopHost);
  const translateX = worldTranslateXPx(layout);

  if (motionBindings?.current != null) {
    motionBindings.current.courierScreenX = playfield.courierScreenX;
  }

  return (
    <div className="level-playfield" {...pointerHandlers}>
      <LevelPlayfieldLayers
        layout={layout}
        level={level}
        player={player}
        tilt={tilt}
        characterId={characterId}
        giantId={giantId}
        isPaused={isPaused}
        translateX={translateX}
        motionBindings={motionBindings}
      />
    </div>
  );
}
